import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  changeCurrentMemories,
  changeCurrentThread,
  computeModuleSpecsMap,
  selectCurrentThread,
  selectCustomerId,
  setIsDirty,
  setRevisionId,
  setShowLoadingIndicator,
  transformThread,
} from './threadsUiSlice';
import { getTypeSpecs } from './typeSpecsActions';
import { handleError, saveThread } from './threadActions';
import { navigateTo } from './navigationActions';
import { cloneDeep } from 'lodash';
import sidekickRevisionsResource from 'src/actions/sidekick/sidekickRevisionsResource';
import SidekickThread from 'src/models/Sidekick/SidekickThreads';

export const saveRevision = createAsyncThunk(
  'threadUi/saveRevision',
  async ({ thread, reason }: { thread: SidekickThread; reason: string }, { dispatch, getState }: any) => {
    const customerId = selectCustomerId(getState());

    const { add, fetchAll } = sidekickRevisionsResource.effects;

    const copyThread = cloneDeep(thread) as unknown as Partial<SidekickThread>;
    const data = { nextData: copyThread.nextData, flowID: copyThread.id, reason };

    await dispatch(
      add({ customerId, ...data }, data, {
        disableWindowBack: true,
        withoutToast: true,
      })
    );

    await dispatch(
      fetchAll(
        { customerId },
        {},
        { body: { idDesc: true, limit: 100, flowID: copyThread.id }, headers: { 'X-Method': 'GET' } }
      )
    );
  }
);

export const setCurrentRevision = createAsyncThunk(
  'threadUi/setCurrentRevision',
  async (
    { revisionId, history }: { revisionId: number; history },
    { dispatch, getState, extra: { gateways } }: any
  ) => {
    try {
      dispatch(setShowLoadingIndicator(true));
      const customerId = selectCustomerId(getState());

      const revision = await gateways.sidekickRevisions.fetch({ id: revisionId, customerId });

      dispatch(setRevisionId(revisionId));

      const currentThread = selectCurrentThread(getState());

      const changedThread = transformThread({ ...currentThread, nextData: revision.data.nextData }, true);

      dispatch(changeCurrentThread(changedThread));

      await dispatch(computeModuleSpecsMap());
      await dispatch(getTypeSpecs());
      await dispatch(changeCurrentMemories({ moduleIndex: 0, modifierIndex: -1 }));
      dispatch(setIsDirty(false));

      dispatch(navigateTo({ history }));
    } catch (e) {
      handleError(e, 'There was an error while fetching the revision.', dispatch);
    } finally {
      dispatch(setShowLoadingIndicator(false));
    }
  }
);

export const navigateToLatestRevision = createAsyncThunk(
  'threadUi/navigateToLatestRevision',
  async ({ history }: { history }, { dispatch, getState, extra: { gateways } }: any) => {
    try {
      dispatch(setShowLoadingIndicator(true));

      dispatch(setRevisionId(null));
      const currentThread = selectCurrentThread(getState());
      const customerId = selectCustomerId(getState());

      const rawThread = await gateways.sidekickThreads.fetch({ id: currentThread.id, customerId });

      dispatch(changeCurrentThread(rawThread));

      await dispatch(computeModuleSpecsMap());

      await dispatch(getTypeSpecs());
      await dispatch(changeCurrentMemories({ moduleIndex: 0, modifierIndex: -1 }));
      dispatch(setIsDirty(false));

      dispatch(navigateTo({ history }));
    } catch (e) {
      handleError(e, 'There was an error while fetching the revision.', dispatch);
    } finally {
      dispatch(setShowLoadingIndicator(false));
    }
  }
);

export const restoreRevision = createAsyncThunk(
  'threadUi/navigateToLatestRevision',
  async ({ history }: { history }, { dispatch, getState }) => {
    const state = getState() as unknown as {
      threadUi;
      sidekickExternalActions;
      sidekickModuleSpecs;
      sidekickRevisions;
    };

    const revisionId = selectRevisionID(getState());
    if (!revisionId) {
      return;
    }

    const currentThread = selectCurrentThread(getState());
    const { values: revisions } = state.sidekickRevisions;
    const revision = revisions.find(rev => rev.id === revisionId);

    const changedThread = transformThread({ ...currentThread, nextData: revision.nextData }, true);

    dispatch(changeCurrentThread(changedThread));

    dispatch(saveThread({ revisionName: `Restore revision ${revision.reason}` }));
    dispatch(setRevisionId(null));
    dispatch(navigateTo({ history }));
  }
);

export const selectRevisionID = state => state.threadUi.revisionId;
